import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { UserPlus, Search, Loader2, Upload, ExternalLink, Download, Edit, Eye, Trash2 } from 'lucide-react';
import { ALL_LOCATIONS } from '@/utils/locations';
import { convertToCSV, downloadCSV, formatDateForCSV } from '@/utils/csvExport';
import { downloadDocument, viewSignedDocument, downloadSignedDocument } from '@/utils/fileDownload';
import DriverAgreementSection from './DriverAgreementSection';

const Drivers = () => {
  const { API } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    location: '',
    driver_type: 'Single',
    dob: '',
    father_name: '',
    pan_number: '',
    aadhar_number: '',
    dl_number: '',
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    reference1_mobile: '',
    reference1_name: '',
    reference1_relation: '',
    reference2_mobile: '',
    reference2_name: '',
    reference2_relation: ''
  });
  const [files, setFiles] = useState({
    driver_photo: null,
    pan: null,
    aadhar: null,
    aadhar_back: null,
    dl: null,
    dl_back: null
  });
  const [submitting, setSubmitting] = useState(false);

  const initialFormData = {
    name: '',
    mobile: '',
    location: '',
    driver_type: 'Single',
    dob: '',
    father_name: '',
    pan_number: '',
    aadhar_number: '',
    dl_number: '',
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    reference1_mobile: '',
    reference1_name: '',
    reference1_relation: '',
    reference2_mobile: '',
    reference2_name: '',
    reference2_relation: ''
  };

  const initialFiles = {
    driver_photo: null,
    pan: null,
    aadhar: null,
    aadhar_back: null,
    dl: null,
    dl_back: null
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Auto-reset to full list when search input is cleared
  useEffect(() => {
    if (search === '') {
      fetchDrivers();
    }
  }, [search]);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/drivers`, {
        params: search ? { search } : {}
      });
      setDrivers(response.data);
    } catch (error) {
      toast.error('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchDrivers();
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      setFiles({ ...files, [type]: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate account numbers match (only for new drivers or if account changed)
    if (formData.account_number && formData.confirm_account_number) {
      if (formData.account_number !== formData.confirm_account_number) {
        toast.error('Account numbers do not match');
        return;
      }
    }

    // Validate IFSC if provided
    if (formData.ifsc_code) {
      try {
        const ifscCheck = await axios.post(
          `${API}/drivers/validate-ifsc`,
          new URLSearchParams({ ifsc_code: formData.ifsc_code }),
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        );
        if (!ifscCheck.data.valid) {
          toast.error(ifscCheck.data.message);
          return;
        }
      } catch (error) {
        toast.error('IFSC validation failed');
        return;
      }
    }

    setSubmitting(true);

    try {
      let driverId;

      if (editMode && editingDriverId) {
        // Update existing driver
        const { confirm_account_number, ...updateData } = formData;
        await axios.patch(`${API}/drivers/${editingDriverId}`, updateData);
        driverId = editingDriverId;
        toast.success('Driver details updated successfully');
      } else {
        // Check PAN duplicate for new driver
        if (formData.pan_number) {
          const panCheck = await axios.post(
            `${API}/drivers/check-pan`,
            new URLSearchParams({ pan_number: formData.pan_number }),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
          );
          if (panCheck.data.exists) {
            toast.error('Driver already exists in the system');
            setSubmitting(false);
            return;
          }
        }

        // Remove confirm_account_number before submission
        const { confirm_account_number, ...submitData } = formData;

        // Create driver
        const response = await axios.post(`${API}/drivers`, submitData);
        driverId = response.data.id;
        toast.success('Driver onboarded successfully');
      }

      // Upload files (for both new and edit)
      for (const [type, file] of Object.entries(files)) {
        if (file) {
          const base64 = await fileToBase64(file);
          await axios.post(`${API}/drivers/${driverId}/upload`, {
            file_name: file.name,
            file_data: base64,
            document_type: type
          });
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save driver details');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFiles(initialFiles);
    setEditMode(false);
    setEditingDriverId(null);
  };

  const handleNewDriver = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditDriver = (driver) => {
    setEditMode(true);
    setEditingDriverId(driver.id);
    setFormData({
      name: driver.name || '',
      mobile: driver.mobile || '',
      location: driver.location || '',
      driver_type: driver.driver_type || 'Single',
      dob: driver.dob || '',
      father_name: driver.father_name || '',
      pan_number: driver.pan_number || '',
      aadhar_number: driver.aadhar_number || '',
      dl_number: driver.dl_number || '',
      bank_name: driver.bank_name || '',
      account_holder_name: driver.account_holder_name || '',
      account_number: driver.account_number || '',
      confirm_account_number: driver.account_number || '',
      ifsc_code: driver.ifsc_code || '',
      reference1_mobile: driver.reference1_mobile || '',
      reference1_name: driver.reference1_name || '',
      reference1_relation: driver.reference1_relation || '',
      reference2_mobile: driver.reference2_mobile || '',
      reference2_name: driver.reference2_name || '',
      reference2_relation: driver.reference2_relation || ''
    });
    setFiles(initialFiles);
    setSelectedDriver(driver);
    setDialogOpen(true);
  };

  const handleViewDriver = async (driver) => {
    setSelectedDriver(driver);
    setViewDialogOpen(true);
  };

  const handleDeleteDriver = async (driver) => {
    if (!window.confirm(`Permanently delete driver ${driver.name} (${driver.driver_id})?\n\nThis cannot be undone. Mobile and Driver ID become available for reuse.`)) return;
    try {
      await axios.delete(`${API}/drivers/${driver.id}`);
      toast.success('Driver deleted');
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete driver');
    }
  };

  const handleExportCSV = () => {
    const headers = [
      { label: 'Driver ID', key: 'driver_id' },
      { label: 'Name', key: 'name' },
      { label: 'Driver Type', key: 'driver_type' },
      { label: 'Father / Guardian Name', key: 'father_name' },
      { label: 'Date of Birth', key: 'dob' },
      { label: 'Mobile Number', key: 'mobile' },
      { label: 'Location', key: 'location' },
      { label: 'PAN Number', key: 'pan_number' },
      { label: 'Aadhar Number', key: 'aadhar_number' },
      { label: 'DL Number', key: 'dl_number' },
      { label: 'Bank Name', key: 'bank_name' },
      { label: 'Account Holder Name', key: 'account_holder_name' },
      { label: 'Account Number', key: 'account_number' },
      { label: 'IFSC Code', key: 'ifsc_code' },
      { label: 'Reference 1 Name', key: 'reference1_name' },
      { label: 'Reference 1 Mobile', key: 'reference1_mobile' },
      { label: 'Reference 1 Relation', key: 'reference1_relation' },
      { label: 'Reference 2 Name', key: 'reference2_name' },
      { label: 'Reference 2 Mobile', key: 'reference2_mobile' },
      { label: 'Reference 2 Relation', key: 'reference2_relation' },
      { label: 'Created At', key: 'created_at' }
    ];

    const csvData = drivers.map(driver => ({
      ...driver,
      created_at: formatDateForCSV(driver.created_at)
    }));

    const csv = convertToCSV(csvData, headers);
    const filename = `drivers_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
    toast.success('Drivers data exported successfully');
  };

  return (
    <div className="space-y-6" data-testid="drivers-section">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Driver Management</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search drivers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-64 input-focus"
                  data-testid="driver-search-input"
                />
                <Button onClick={handleSearch} variant="outline" data-testid="search-btn">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={drivers.length === 0}
                data-testid="export-drivers-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                className="btn-primary"
                onClick={handleNewDriver}
                data-testid="new-driver-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                New Driver Onboarding
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <Table data-testid="drivers-table">
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Driver ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Mobile Number</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.map((driver) => (
                  <TableRow key={driver.id} className="table-row">
                    <TableCell>
                      <button
                        onClick={() => handleViewDriver(driver)}
                        className="font-mono text-blue-600 hover:underline"
                        data-testid={`driver-id-link-${driver.driver_id}`}
                      >
                        {driver.driver_id}
                      </button>
                    </TableCell>
                    <TableCell className="font-medium">{driver.name}</TableCell>
                    <TableCell className="font-mono">{driver.mobile}</TableCell>
                    <TableCell>{driver.location}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDriver(driver)}
                          data-testid={`view-driver-${driver.id}`}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditDriver(driver)}
                          data-testid={`edit-driver-${driver.id}`}
                          title="Edit Driver"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteDriver(driver)}
                          data-testid={`delete-driver-${driver.id}`}
                          title="Delete Permanently"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {drivers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      No drivers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Driver Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="driver-form-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Driver Details' : 'New Driver Onboarding'}</DialogTitle>
            <DialogDescription>
              {editMode 
                ? 'Update driver details and upload any missing documents' 
                : 'Fill in driver details and upload required documents'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="driver-name-input"
                  className="input-focus"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                  data-testid="driver-mobile-input"
                  className="input-focus"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <select
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                  data-testid="driver-location-select"
                >
                  <option value="">Select location</option>
                  {ALL_LOCATIONS.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver_type">Driver Type *</Label>
                <select
                  id="driver_type"
                  value={formData.driver_type}
                  onChange={(e) => setFormData({ ...formData, driver_type: e.target.value })}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                  data-testid="driver-type-select"
                >
                  <option value="Single">Single Driver</option>
                  <option value="Vendor">Vendor</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  data-testid="driver-dob-input"
                  className="input-focus"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="father_name">Father&apos;s / Guardian&apos;s Name</Label>
                <Input
                  id="father_name"
                  value={formData.father_name}
                  onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                  placeholder="Full name as per records"
                  data-testid="driver-father-name-input"
                  className="input-focus"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pan_number">PAN Number</Label>
                <Input
                  id="pan_number"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                  placeholder="ABCDE1234F"
                  data-testid="driver-pan-input"
                  className="input-focus"
                  disabled={editMode && selectedDriver?.pan_number}
                />
                {editMode && selectedDriver?.pan_number && (
                  <p className="text-xs text-gray-500">PAN cannot be changed</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="aadhar_number">Aadhar Number</Label>
                <Input
                  id="aadhar_number"
                  value={formData.aadhar_number}
                  onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })}
                  placeholder="1234 5678 9012"
                  data-testid="driver-aadhar-input"
                  className="input-focus"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dl_number">DL Number</Label>
                <Input
                  id="dl_number"
                  value={formData.dl_number}
                  onChange={(e) => setFormData({ ...formData, dl_number: e.target.value })}
                  data-testid="driver-dl-input"
                  className="input-focus"
                />
              </div>
            </div>

            {/* Bank Details Section */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Bank Details</h4>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="e.g., State Bank of India"
                      data-testid="bank-name-input"
                      className="input-focus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account_holder_name">Account Holder Name</Label>
                    <Input
                      id="account_holder_name"
                      value={formData.account_holder_name}
                      onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                      placeholder="As per bank records"
                      data-testid="account-holder-input"
                      className="input-focus"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="account_number">Account Number</Label>
                    <Input
                      id="account_number"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      placeholder="Enter account number"
                      data-testid="account-number-input"
                      className="input-focus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_account_number">Confirm Account Number</Label>
                    <Input
                      id="confirm_account_number"
                      value={formData.confirm_account_number}
                      onChange={(e) => setFormData({ ...formData, confirm_account_number: e.target.value })}
                      placeholder="Re-enter account number"
                      data-testid="confirm-account-number-input"
                      className={`input-focus ${formData.account_number && formData.confirm_account_number && formData.account_number !== formData.confirm_account_number ? 'border-red-500' : ''}`}
                    />
                    {formData.account_number && formData.confirm_account_number && formData.account_number !== formData.confirm_account_number && (
                      <p className="text-xs text-red-500">Account numbers do not match</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ifsc_code">IFSC Code</Label>
                    <Input
                      id="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value.toUpperCase() })}
                      placeholder="e.g., SBIN0001234"
                      data-testid="ifsc-code-input"
                      className="input-focus"
                      maxLength={11}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Reference Section */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Reference Contacts (2 Required)</h4>
              
              {/* Reference 1 */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h5 className="text-sm font-medium mb-3 text-gray-700">Reference 1</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reference1_name">Name</Label>
                    <Input
                      id="reference1_name"
                      value={formData.reference1_name}
                      onChange={(e) => setFormData({ ...formData, reference1_name: e.target.value })}
                      placeholder="Full Name"
                      data-testid="reference1-name-input"
                      className="input-focus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reference1_mobile">Mobile Number</Label>
                    <Input
                      id="reference1_mobile"
                      type="tel"
                      value={formData.reference1_mobile}
                      onChange={(e) => setFormData({ ...formData, reference1_mobile: e.target.value })}
                      placeholder="9876543210"
                      data-testid="reference1-mobile-input"
                      className="input-focus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reference1_relation">Relation</Label>
                    <select
                      id="reference1_relation"
                      value={formData.reference1_relation}
                      onChange={(e) => setFormData({ ...formData, reference1_relation: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                      data-testid="reference1-relation-select"
                    >
                      <option value="">Select relation</option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Brother">Brother</option>
                      <option value="Sister">Sister</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Son">Son</option>
                      <option value="Daughter">Daughter</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Reference 2 */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="text-sm font-medium mb-3 text-gray-700">Reference 2</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reference2_name">Name</Label>
                    <Input
                      id="reference2_name"
                      value={formData.reference2_name}
                      onChange={(e) => setFormData({ ...formData, reference2_name: e.target.value })}
                      placeholder="Full Name"
                      data-testid="reference2-name-input"
                      className="input-focus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reference2_mobile">Mobile Number</Label>
                    <Input
                      id="reference2_mobile"
                      type="tel"
                      value={formData.reference2_mobile}
                      onChange={(e) => setFormData({ ...formData, reference2_mobile: e.target.value })}
                      placeholder="9876543210"
                      data-testid="reference2-mobile-input"
                      className="input-focus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reference2_relation">Relation</Label>
                    <select
                      id="reference2_relation"
                      value={formData.reference2_relation}
                      onChange={(e) => setFormData({ ...formData, reference2_relation: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                      data-testid="reference2-relation-select"
                    >
                      <option value="">Select relation</option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Brother">Brother</option>
                      <option value="Sister">Sister</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Son">Son</option>
                      <option value="Daughter">Daughter</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Upload Documents</h4>
              {editMode && selectedDriver && (
                <p className="text-sm text-gray-500 mb-4">
                  Only upload documents you want to add or replace. Existing documents will be preserved.
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="driver_photo">
                    Driver Photo
                    {editMode && selectedDriver?.driver_photo && (
                      <span className="ml-2 text-xs text-green-600">(Uploaded)</span>
                    )}
                  </Label>
                  <Input
                    id="driver_photo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('driver_photo', e)}
                    data-testid="driver-photo-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pan">
                    PAN Card
                    {editMode && selectedDriver?.pan_photo && (
                      <span className="ml-2 text-xs text-green-600">(Uploaded)</span>
                    )}
                  </Label>
                  <Input
                    id="pan"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('pan', e)}
                    data-testid="pan-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aadhar">
                    Aadhar Card (Front)
                    {editMode && selectedDriver?.aadhar_photo && (
                      <span className="ml-2 text-xs text-green-600">(Uploaded)</span>
                    )}
                  </Label>
                  <Input
                    id="aadhar"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('aadhar', e)}
                    data-testid="aadhar-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aadhar_back">
                    Aadhar Card (Back)
                    {editMode && selectedDriver?.aadhar_back_photo && (
                      <span className="ml-2 text-xs text-green-600">(Uploaded)</span>
                    )}
                  </Label>
                  <Input
                    id="aadhar_back"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('aadhar_back', e)}
                    data-testid="aadhar-back-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dl">
                    Driving License (Front)
                    {editMode && selectedDriver?.dl_photo && (
                      <span className="ml-2 text-xs text-green-600">(Uploaded)</span>
                    )}
                  </Label>
                  <Input
                    id="dl"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('dl', e)}
                    data-testid="dl-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dl_back">
                    Driving License (Back)
                    {editMode && selectedDriver?.dl_back_photo && (
                      <span className="ml-2 text-xs text-green-600">(Uploaded)</span>
                    )}
                  </Label>
                  <Input
                    id="dl_back"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('dl_back', e)}
                    data-testid="dl-back-upload"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={submitting}
              data-testid="submit-driver-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editMode ? 'Update Driver Details' : 'Submit Driver Details'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Driver Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-driver-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Driver Details</DialogTitle>
              {selectedDriver && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleEditDriver(selectedDriver);
                  }}
                  data-testid="edit-from-view-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
            <DialogDescription>View driver profile, bank details, references, and uploaded documents</DialogDescription>
          </DialogHeader>
          {selectedDriver && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Driver ID</Label>
                  <p className="font-mono font-semibold">{selectedDriver.driver_id}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Name</Label>
                  <p className="font-semibold">{selectedDriver.name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Mobile</Label>
                  <p className="font-mono">{selectedDriver.mobile}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Location</Label>
                  <p>{selectedDriver.location}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Driver Type</Label>
                  <p>{selectedDriver.driver_type || 'Single'}</p>
                </div>
                {selectedDriver.dob && (
                  <div>
                    <Label className="text-gray-500">Date of Birth</Label>
                    <p>{selectedDriver.dob}</p>
                  </div>
                )}
                {selectedDriver.father_name && (
                  <div>
                    <Label className="text-gray-500">Father&apos;s / Guardian&apos;s Name</Label>
                    <p>{selectedDriver.father_name}</p>
                  </div>
                )}
                {selectedDriver.pan_number && (
                  <div>
                    <Label className="text-gray-500">PAN Number</Label>
                    <p className="font-mono">{selectedDriver.pan_number}</p>
                  </div>
                )}
                {selectedDriver.aadhar_number && (
                  <div>
                    <Label className="text-gray-500">Aadhar Number</Label>
                    <p className="font-mono">{selectedDriver.aadhar_number}</p>
                  </div>
                )}
                {selectedDriver.dl_number && (
                  <div>
                    <Label className="text-gray-500">DL Number</Label>
                    <p className="font-mono">{selectedDriver.dl_number}</p>
                  </div>
                )}
              </div>
              
              {/* Bank Details Section */}
              {(selectedDriver.bank_name || selectedDriver.account_number) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Bank Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedDriver.bank_name && (
                      <div>
                        <Label className="text-gray-500">Bank Name</Label>
                        <p className="font-medium">{selectedDriver.bank_name}</p>
                      </div>
                    )}
                    {selectedDriver.account_holder_name && (
                      <div>
                        <Label className="text-gray-500">Account Holder Name</Label>
                        <p className="font-medium">{selectedDriver.account_holder_name}</p>
                      </div>
                    )}
                    {selectedDriver.account_number && (
                      <div>
                        <Label className="text-gray-500">Account Number</Label>
                        <p className="font-mono">{selectedDriver.account_number}</p>
                      </div>
                    )}
                    {selectedDriver.ifsc_code && (
                      <div>
                        <Label className="text-gray-500">IFSC Code</Label>
                        <p className="font-mono">{selectedDriver.ifsc_code}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reference Contacts Section */}
              {(selectedDriver.reference1_name || selectedDriver.reference2_name) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Reference Contacts</h4>
                  
                  {selectedDriver.reference1_name && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 mb-2">Reference 1</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Name</Label>
                          <p className="font-medium">{selectedDriver.reference1_name}</p>
                        </div>
                        {selectedDriver.reference1_mobile && (
                          <div>
                            <Label className="text-xs text-gray-500">Mobile</Label>
                            <p className="font-mono">{selectedDriver.reference1_mobile}</p>
                          </div>
                        )}
                        {selectedDriver.reference1_relation && (
                          <div>
                            <Label className="text-xs text-gray-500">Relation</Label>
                            <p>{selectedDriver.reference1_relation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {selectedDriver.reference2_name && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 mb-2">Reference 2</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Name</Label>
                          <p className="font-medium">{selectedDriver.reference2_name}</p>
                        </div>
                        {selectedDriver.reference2_mobile && (
                          <div>
                            <Label className="text-xs text-gray-500">Mobile</Label>
                            <p className="font-mono">{selectedDriver.reference2_mobile}</p>
                          </div>
                        )}
                        {selectedDriver.reference2_relation && (
                          <div>
                            <Label className="text-xs text-gray-500">Relation</Label>
                            <p>{selectedDriver.reference2_relation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documents Section */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Uploaded Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { key: 'driver_photo', label: 'Driver Photo' },
                    { key: 'pan_photo', label: 'PAN Card' },
                    { key: 'aadhar_photo', label: 'Aadhar (Front)' },
                    { key: 'aadhar_back_photo', label: 'Aadhar (Back)' },
                    { key: 'dl_photo', label: 'DL (Front)' },
                    { key: 'dl_back_photo', label: 'DL (Back)' },
                  ].map((doc) => {
                    const url = selectedDriver[doc.key];
                    const filename = `${selectedDriver.driver_id}_${doc.key}`;
                    const endpoint = `/drivers/${selectedDriver.id}/file-url/${doc.key}`;
                    return (
                      <div
                        key={doc.key}
                        className="flex items-center justify-between gap-3 px-3 py-2 border rounded-md bg-gray-50"
                        data-testid={`doc-row-${doc.key}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${url ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span className="text-sm font-medium truncate">{doc.label}</span>
                        </div>
                        {url ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => viewSignedDocument(API, endpoint)}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline px-2 py-1 rounded"
                              data-testid={`view-${doc.key}`}
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadSignedDocument(API, endpoint, filename)}
                              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 hover:underline px-2 py-1 rounded"
                              data-testid={`download-${doc.key}`}
                            >
                              <Download className="w-3 h-3" />
                              Download
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 flex-shrink-0">Not uploaded</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <DriverAgreementSection API={API} driver={selectedDriver} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Drivers;
